import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { localDateSchema, moneySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Bütçe (Prompt 11 §9; özellik bayrağı `EXPENSES_BUDGET`).
 *
 * Hücre: şirket / istasyon / iş birimi × hesap grubu (hesap kodu ÖNEKİ) × ay. Tutar hesabın DOĞAL yönündedir
 * (gelir: alacak − borç, gider: borç − alacak). Sapma yalnızca OLUMSUZ yönde ve eşik AŞILINCA alarmdır;
 * bütçesiz harcama her zaman alarmdır (`budget/domain/variance.ts`).
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const queryBoolean = z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true");
export const BUDGET_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"];
export const budgetStatusSchema = z.enum(BUDGET_STATUSES);
export const BUDGET_STATUS_LABELS = {
    DRAFT: "Taslak",
    ACTIVE: "Yürürlükte",
    ARCHIVED: "Arşivlendi",
};
export const BUDGET_NATURES = ["REVENUE", "EXPENSE"];
export const budgetNatureSchema = z.enum(BUDGET_NATURES);
export const BUDGET_NATURE_LABELS = {
    REVENUE: "Gelir",
    EXPENSE: "Gider",
};
/** Hesap grubu öneki: 600, 600.01, 770.03 */
export const accountPrefixSchema = z
    .string()
    .trim()
    .regex(/^\d{1,3}(\.\d{1,4}){0,3}$/, "Hesap grubu 600, 600.01 ya da 770.03 biçiminde olmalıdır");
export const monthSchema = z.number().int().min(1).max(12);
// ── Bütçe başlığı ────────────────────────────────────────────────────────────
export const budgetSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    fiscalYear: z.number().int(),
    name: z.string(),
    versionNo: z.number().int(),
    status: budgetStatusSchema,
    variancePercent: rateSchema.describe("Sapma alarmı eşiği (%)"),
    importFileId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    lineCount: z.number().int(),
    totalAmount: moneySchema.describe("Tüm hücrelerin toplamı (kuruş)"),
    createdAt: timestampSchema,
    version: versionSchema,
});
export const budgetListSchema = z.object({ items: z.array(budgetSchema) });
export const listBudgetsQuerySchema = z.object({
    companyId: uuidSchema,
    fiscalYear: z.coerce.number().int().min(2000).max(2999).optional(),
    status: budgetStatusSchema.optional(),
});
export const createBudgetSchema = z.object({
    companyId: uuidSchema,
    fiscalYear: z.number().int().min(2000).max(2999),
    name: z.string().trim().min(1).max(150),
    variancePercent: rateSchema.default("10"),
    notes: z.string().trim().max(1000).nullable().default(null),
});
/** Yıl, sürüm ve durum burada DEĞİŞMEZ (ayrı uçlar). */
export const updateBudgetSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    variancePercent: rateSchema.optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    version: versionSchema,
});
export const activateBudgetSchema = z.object({ version: versionSchema });
/** Mevcut bütçeden YENİ SÜRÜM (satırlar kopyalanır, durum DRAFT, `version_no + 1`). */
export const createBudgetVersionSchema = z.object({
    name: z.string().trim().min(1).max(150).optional().describe("Boşsa kaynak bütçenin adı"),
    version: versionSchema,
});
// ── Satırlar (hücreler) ──────────────────────────────────────────────────────
export const budgetLineSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema.nullable().describe("null = şirket geneli"),
    stationName: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    accountPrefix: z.string(),
    nature: budgetNatureSchema,
    month: z.number().int(),
    amount: moneySchema,
});
export const listBudgetLinesQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    accountPrefix: accountPrefixSchema.optional(),
});
export const budgetLineListSchema = z.object({
    items: z.array(budgetLineSchema),
    totalAmount: moneySchema,
    /** Hesap planında karşılığı olmayan önekler (engel değil, uyarı). */
    warnings: z.array(z.string()),
});
export const budgetLineInputSchema = z.object({
    stationId: uuidSchema.nullable().default(null),
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    accountPrefix: accountPrefixSchema,
    month: monthSchema,
    amount: moneySchema.refine((value) => BigInt(value) >= 0n, "Bütçe tutarı negatif olamaz"),
});
/**
 * PUT: TASLAK bütçenin TÜM satırlarını değiştirir (kısmi güncelleme değildir, bu yüzden adı `update…Schema`
 * DEĞİLDİR — `common/patch.test.ts` yalnızca PATCH şemalarını denetler).
 */
export const saveBudgetLinesSchema = z.object({
    lines: z.array(budgetLineInputSchema).max(5000),
    version: versionSchema,
});
// ── Excel / CSV içe aktarma ──────────────────────────────────────────────────
export const BUDGET_IMPORT_MONTH_HEADERS = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
];
export const BUDGET_IMPORT_COLUMNS = [
    "Hesap",
    "İstasyon Kodu",
    "İş Birimi",
    ...BUDGET_IMPORT_MONTH_HEADERS,
];
export const importBudgetSchema = z.object({
    fileId: uuidSchema.describe("`IMPORT` amacıyla yüklenmiş XLSX ya da CSV"),
    dryRun: z
        .boolean()
        .default(true)
        .describe("true: yalnızca önizleme; false: TASLAK bütçenin satırlarını değiştirir"),
    version: versionSchema,
});
export const budgetImportIssueSchema = z.object({
    rowNumber: z.number().int().describe("Dosyadaki gerçek satır numarası"),
    column: z.string().nullable(),
    message: z.string(),
});
export const budgetImportPreviewSchema = z.object({
    budgetId: uuidSchema,
    dryRun: z.boolean(),
    /** Dosyadan okunan geçerli hücre sayısı. */
    cellCount: z.number().int(),
    rowCount: z.number().int(),
    totalAmount: moneySchema,
    byNature: z.object({ revenue: moneySchema, expense: moneySchema }),
    errors: z.array(budgetImportIssueSchema),
    warnings: z.array(budgetImportIssueSchema),
    /** `dryRun: false` ve hata yoksa uygulanan satır sayısı. */
    appliedLineCount: z.number().int().nullable(),
});
// ── Gerçekleşen – bütçe raporu ───────────────────────────────────────────────
export const BUDGET_REPORT_GROUPINGS = ["ACCOUNT", "STATION"];
export const budgetReportGroupBySchema = z.enum(BUDGET_REPORT_GROUPINGS);
export const actualVsBudgetQuerySchema = z.object({
    budgetId: uuidSchema,
    throughMonth: z.coerce.number().int().min(1).max(12).optional().describe("Boşsa içinde bulunulan ay"),
    stationId: uuidSchema.optional(),
    groupBy: budgetReportGroupBySchema.default("ACCOUNT"),
    includeMonths: queryBoolean.describe("Ay ay döküm de dönsün mü"),
});
export const varianceSchema = z.object({
    budget: moneySchema,
    actual: moneySchema,
    difference: moneySchema.describe("gerçekleşen − bütçe"),
    percent: z.string().nullable().describe("Bütçeye göre yüzde; bütçe 0 ise null"),
    unfavourable: z.boolean(),
    exceedsThreshold: z.boolean(),
});
export const actualVsBudgetRowSchema = z.object({
    key: z.string(),
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    accountPrefix: z.string(),
    accountName: z.string().nullable(),
    nature: budgetNatureSchema,
    /** Bütçesi olmayan ama gerçekleşeni olan hücre. */
    unbudgeted: z.boolean(),
    yearToDate: varianceSchema,
    /** `includeMonths=true` ise dolu; her ay için sapma da hesaplanmıştır (yıl içi toplamla aynı saf fonksiyon). */
    months: z.array(z.object({ month: z.number().int() }).extend(varianceSchema.shape)),
});
export const actualVsBudgetSchema = z.object({
    budgetId: uuidSchema,
    companyId: uuidSchema,
    fiscalYear: z.number().int(),
    throughMonth: z.number().int(),
    groupBy: budgetReportGroupBySchema,
    thresholdPercent: rateSchema,
    rows: z.array(actualVsBudgetRowSchema),
    totals: z.object({ revenue: varianceSchema, expense: varianceSchema }),
});
export const budgetVarianceRunResultSchema = z.object({
    budgetsChecked: z.number().int(),
    alertsRaised: z.number().int(),
    month: z.number().int().nullable(),
    failed: z.array(z.object({ budgetId: uuidSchema, error: z.string() })),
});
/** Rapor / alarm çıktısında ay etiketleri. */
export const BUDGET_MONTH_LABELS = BUDGET_IMPORT_MONTH_HEADERS;
export const budgetImportTemplateQuerySchema = z.object({
    format: z.enum(["CSV"]).default("CSV"),
});
/** Rapor uçlarında ortak: bütçe yılının ay aralığı. */
export const budgetMonthRangeSchema = z.object({ from: localDateSchema, to: localDateSchema });
//# sourceMappingURL=budget.js.map